import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Cloud, Droplets, Wind, Thermometer, ExternalLink, BarChart3, Shield, AlertTriangle, RefreshCw, Sun, CloudSun, CloudFog, CloudDrizzle, CloudRain, Snowflake, CloudLightning } from "lucide-react";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const B = {
  navy: C.navy, orange: C.orange, blue: C.blue, lilac: C.lilac,
  olive: C.olive, pink: C.pink, cream: C.offwhite,
  heading: DISPLAY,
  mono: MONO,
};

const WMO_CODES = {
  0: { text: "Despejado", icon: "sun" },
  1: { text: "Mayormente despejado", icon: "sun" },
  2: { text: "Parcialmente nublado", icon: "cloud-sun" },
  3: { text: "Nublado", icon: "cloud" },
  45: { text: "Niebla", icon: "cloud-fog" },
  48: { text: "Niebla helada", icon: "cloud-fog" },
  51: { text: "Llovizna ligera", icon: "cloud-drizzle" },
  53: { text: "Llovizna", icon: "cloud-drizzle" },
  55: { text: "Llovizna intensa", icon: "cloud-drizzle" },
  61: { text: "Lluvia ligera", icon: "cloud-rain" },
  63: { text: "Lluvia", icon: "cloud-rain" },
  65: { text: "Lluvia intensa", icon: "cloud-rain" },
  71: { text: "Nieve ligera", icon: "snowflake" },
  73: { text: "Nieve", icon: "snowflake" },
  75: { text: "Nieve intensa", icon: "snowflake" },
  80: { text: "Chubascos", icon: "cloud-rain" },
  81: { text: "Chubascos fuertes", icon: "cloud-rain" },
  82: { text: "Chubascos violentos", icon: "cloud-rain" },
  95: { text: "Tormenta", icon: "cloud-lightning" },
  96: { text: "Tormenta con granizo", icon: "cloud-lightning" },
  99: { text: "Tormenta fuerte con granizo", icon: "cloud-lightning" },
};

function WeatherIcon({ code, size = 20, style }) {
  const I =
    code <= 1 ? Sun :
    code <= 3 ? CloudSun :
    code <= 48 ? CloudFog :
    code <= 55 ? CloudDrizzle :
    code <= 65 ? CloudRain :
    code <= 75 ? Snowflake :
    code <= 82 ? CloudRain : CloudLightning;
  return <I size={size} style={style} />;
}

function Card({ children, className = "", bg = "#fff", accent = B.navy }) {
  return (
    <div className={className} style={{ background: bg, border: `3px solid ${accent}`, boxShadow: `6px 6px 0 ${accent}` }}>
      {children}
    </div>
  );
}

function Tag({ children, bg = B.navy, color = "#fff" }) {
  return <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase" style={{ fontFamily: B.mono, background: bg, color }}>{children}</span>;
}

const ICON_MAP = {
  "alert-triangle": AlertTriangle,
  "cloud": Cloud,
  "radar": RefreshCw,
  "shield": Shield,
  "bar-chart": BarChart3,
  "droplets": Droplets,
};

const DEFAULT_COORDS = { ES: { lat: 40.42, lng: -3.70, name: "Madrid" }, EC: { lat: -2.19, lng: -79.89, name: "Guayaquil" } };

export default function WeatherPage() {
  const navigate = useNavigate();
  const [country, setCountry] = useState(() => localStorage.getItem("emergencia_country")?.toUpperCase() || "ES");
  const [weather, setWeather] = useState(null);
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInamhi, setShowInamhi] = useState(false);

  const coords = DEFAULT_COORDS[country] || DEFAULT_COORDS.ES;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/weather?lat=${coords.lat}&lng=${coords.lng}&country=${country}`),
      axios.get(`${API}/weather/resources/${country}`),
    ]).then(([w, r]) => {
      setWeather(w.data);
      setResources(r.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [country, coords.lat, coords.lng]);

  const current = weather?.current;
  const daily = weather?.daily;
  const wmoInfo = current ? (WMO_CODES[current.weather_code] || { text: "Desconocido" }) : null;

  return (
    <div className="min-h-screen" style={{ background: B.cream }}>
      {/* HEADER */}
      <div style={{ background: B.navy, borderBottom: `4px solid ${B.blue}` }}>
        <div className="max-w-5xl mx-auto px-4 py-5">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(-1)} style={{ color: B.cream }}><ArrowLeft size={22} /></button>
            <div>
              <h1 className="text-2xl md:text-3xl font-black uppercase" style={{ fontFamily: B.heading, color: B.cream }}>
                TIEMPO Y ALERTAS
              </h1>
              <p className="text-xs uppercase tracking-wider" style={{ fontFamily: B.mono, color: B.lilac }}>
                Meteorología · Recursos · Radar
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {["ES", "EC"].map((c) => (
              <button key={c} onClick={() => setCountry(c)} data-testid={`weather-country-${c}`}
                className="px-3 py-1 text-[11px] font-bold uppercase"
                style={{ fontFamily: B.mono, border: `2px solid ${country === c ? B.orange : "#ffffff99"}`,
                  background: country === c ? B.orange : "rgba(255,255,255,0.1)", color: "#fff" }}>
                {c === "ES" ? "ESPAÑA" : "ECUADOR"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">

        {/* CURRENT WEATHER */}
        {loading ? (
          <Card bg="#fff" accent={B.navy} className="p-8 text-center">
            <RefreshCw size={24} className="animate-spin mx-auto" style={{ color: B.blue }} />
            <p className="text-xs mt-2" style={{ fontFamily: B.mono }}>Cargando datos meteorológicos...</p>
          </Card>
        ) : current ? (
          <Card bg={B.navy} accent={B.blue} className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <Tag bg={B.blue}>AHORA · {coords.name}</Tag>
                <div className="flex items-center gap-3 mt-2">
                  <WeatherIcon code={current.weather_code} size={44} style={{ color: B.cream }} />
                  <div>
                    <span className="text-4xl font-black" style={{ fontFamily: B.heading, color: B.cream }}>
                      {Math.round(current.temperature_2m)}°
                    </span>
                    <p className="text-sm font-bold" style={{ fontFamily: B.mono, color: B.lilac }}>
                      {wmoInfo?.text}
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <div className="flex items-center gap-1.5 text-xs" style={{ fontFamily: B.mono, color: B.cream }}>
                  <Droplets size={14} style={{ color: B.blue }} /> {current.relative_humidity_2m}%
                </div>
                <div className="flex items-center gap-1.5 text-xs" style={{ fontFamily: B.mono, color: B.cream }}>
                  <Wind size={14} style={{ color: B.blue }} /> {Math.round(current.wind_speed_10m)} km/h
                </div>
                {current.precipitation !== undefined && (
                  <div className="flex items-center gap-1.5 text-xs" style={{ fontFamily: B.mono, color: B.cream }}>
                    <Cloud size={14} style={{ color: B.blue }} /> {current.precipitation} mm
                  </div>
                )}
              </div>
            </div>

            {/* FORECAST */}
            {daily && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
                {daily.time?.slice(0, 5).map((day, i) => (
                  <div key={day} className="flex-shrink-0 p-2 text-center min-w-[70px]"
                    style={{ background: i === 0 ? B.blue + "33" : B.navy, border: `1px solid ${B.blue}33` }}>
                    <p className="text-[10px] font-bold" style={{ fontFamily: B.mono, color: B.lilac }}>
                      {i === 0 ? "HOY" : new Date(day).toLocaleDateString("es", { weekday: "short" }).toUpperCase()}
                    </p>
                    <WeatherIcon code={daily.weather_code[i]} size={18} style={{ color: B.cream, margin: "4px auto" }} />
                    <div className="text-[11px] font-bold" style={{ fontFamily: B.mono, color: B.cream }}>
                      <span style={{ color: B.orange }}>{Math.round(daily.temperature_2m_max[i])}°</span>
                      <span style={{ color: B.cream + "66" }}> / {Math.round(daily.temperature_2m_min[i])}°</span>
                    </div>
                    {daily.precipitation_sum && (
                      <p className="text-[9px]" style={{ fontFamily: B.mono, color: B.blue }}>
                        {daily.precipitation_sum[i] > 0 ? `${daily.precipitation_sum[i]}mm` : "—"}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ) : (
          <Card bg="#fff" accent={B.navy} className="p-6 text-center">
            <p style={{ fontFamily: B.mono, color: B.navy + "77" }}>No se pudieron cargar los datos meteorológicos</p>
          </Card>
        )}

        {/* INAMHI EMBED for Ecuador */}
        {country === "EC" && (
          <Card bg="#fff" accent={B.blue} className="overflow-hidden">
            <button onClick={() => setShowInamhi(!showInamhi)}
              className="w-full flex items-center justify-between p-4 text-left" data-testid="inamhi-toggle">
              <div>
                <Tag bg={B.blue}>INAMHI</Tag>
                <h3 className="font-black text-sm mt-1 uppercase" style={{ fontFamily: B.heading, color: B.navy }}>
                  Explorador de Datos Meteorológicos Ecuador
                </h3>
                <p className="text-[11px]" style={{ fontFamily: B.mono, color: B.navy + "77" }}>
                  Estaciones, temperatura, precipitación, alertas en tiempo real
                </p>
              </div>
              <span className="text-xs font-bold px-3 py-1" style={{ fontFamily: B.mono, background: B.blue, color: "#fff" }}>
                {showInamhi ? "CERRAR" : "ABRIR VISOR"}
              </span>
            </button>
            {showInamhi && (
              <div>
                <div className="flex gap-1 px-4 py-2" style={{ background: B.navy }}>
                  {[
                    { label: "DATOS METEOROLÓGICOS", url: "https://inamhi.geoglows.org/apps/met-data-explorer" },
                    { label: "ALERTAS", url: "https://inamhi.geoglows.org/" },
                  ].map((view) => (
                    <button key={view.label} onClick={() => {
                      const iframe = document.querySelector('[data-testid="inamhi-iframe"]');
                      if (iframe) iframe.src = view.url;
                    }} className="px-3 py-1 text-[10px] font-bold"
                      style={{ fontFamily: B.mono, background: B.blue, color: "#fff", border: `1px solid ${B.cream}44` }}>
                      {view.label}
                    </button>
                  ))}
                </div>
                <div style={{ height: "500px", borderTop: `3px solid ${B.navy}` }}>
                  <iframe
                    src="https://inamhi.geoglows.org/apps/met-data-explorer"
                    title="INAMHI Explorador Meteorológico"
                    style={{ width: "100%", height: "100%", border: "none" }}
                    data-testid="inamhi-iframe"
                  />
                </div>
              </div>
            )}
          </Card>
        )}

        {/* WEATHER RESOURCES */}
        {resources && (
          <div>
            <h2 className="text-lg font-black uppercase mb-3" style={{ fontFamily: B.heading, color: B.navy }}>
              RECURSOS METEOROLÓGICOS · {resources.name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {resources.services.map((svc) => {
                const IconComp = ICON_MAP[svc.icon] || Cloud;
                return (
                  <a key={svc.url} href={svc.url} target="_blank" rel="noopener noreferrer"
                    className="block" data-testid={`resource-${svc.type}`}>
                    <Card bg="#fff" accent={B.navy} className="p-4 hover:-translate-y-0.5 transition-transform h-full">
                      <div className="flex items-start gap-3">
                        <div className="p-2 shrink-0" style={{ background: B.navy }}>
                          <IconComp size={16} color={B.cream} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-black text-xs uppercase" style={{ fontFamily: B.heading, color: B.navy }}>
                              {svc.name}
                            </h3>
                            <ExternalLink size={11} style={{ color: B.blue }} />
                          </div>
                          <p className="text-[11px] mt-0.5" style={{ fontFamily: B.mono, color: B.navy + "88" }}>
                            {svc.description}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Credit */}
        <p className="text-[10px] text-center" style={{ fontFamily: B.mono, color: B.navy + "44" }}>
          Datos meteorológicos: Open-Meteo API | Recursos: {country === "EC" ? "INAMHI · RADARNET-SUR" : "AEMET"}
        </p>
      </div>

    </div>
  );
}
